// Pure model of the "Cómo queda si se aprueba" preview and of the per-item outcomes of a request. No React,
// relative imports only (the web test runner compiles files one by one). The effect itself is computed by
// `calcularEfectoAdscripcion` from `@siiv/schemas`, the SAME function the server applies: the preview cannot
// diverge from what an approval does (UAR-03).

import {
	calcularEfectoAdscripcion,
	describirItemAdscripcion,
	mensajeViolacionAdscripcion,
	type DecisionAdscripcionEntrada,
	type EstadoAdscripcion,
	type ItemAdscripcion,
	type LineaRespaldo,
	type MotivoAutomaticoAdscripcion,
	type SolicitudResponse,
	type ViolacionAdscripcion,
} from '@siiv/schemas';

export type MarcaUnidad = 'nueva' | 'sin_cambio' | 'se_quita';

export interface UnidadVistaPrevia {
	idUnidad: number;
	nombre: string;
	marca: MarcaUnidad;
	/** The unit is, or was before this change, the principal one (it is kept as additional or it leaves). */
	eraPrincipal: boolean;
}

export interface VistaPreviaAdscripcion {
	principal: { idUnidad: number; nombre: string; cambia: boolean } | null;
	/** The additional units after the change, then the ones that leave. */
	adicionales: UnidadVistaPrevia[];
	lineasCerradas: { idLinea: number; nombre: string }[];
	violaciones: ViolacionAdscripcion[];
	/** `violaciones` worded for people (shared messages). */
	mensajes: string[];
	sinCambios: boolean;
}

export interface EntradaVistaPrevia {
	/** Current state; null for a researcher with no active unit (first assignment). */
	actual: EstadoAdscripcion | null;
	/** Items of the request. Empty when `actual` is null. */
	items: ItemAdscripcion[];
	/** Admin decisions; empty for the researcher's form (everything is approved). */
	decisiones: DecisionAdscripcionEntrada[];
	/** The requested state, when it is not validated yet (the form): an invalid one is reported, not repaired. */
	objetivo?: EstadoAdscripcion;
	/**
	 * The order of the additional units the request asked for (its target's), so a new unit is listed where an approval
	 * will put it (see `ordenarAdicionales`). Without it the new units come last.
	 */
	ordenAdicionales?: readonly number[];
	lineasActivas: Array<LineaRespaldo & { nombreLinea: string | null }>;
	nombres: Map<number, string>;
}

export function construirVistaPrevia(entrada: EntradaVistaPrevia): VistaPreviaAdscripcion {
	const { actual, objetivo, nombres } = entrada;
	const nombreDe = (idUnidad: number) => nombres.get(idUnidad) ?? `Unidad ${idUnidad}`;

	// First assignment: nothing is current, so everything is new and nothing can close.
	if (!actual) {
		if (!objetivo) {
			return { principal: null, adicionales: [], lineasCerradas: [], violaciones: [], mensajes: [], sinCambios: false };
		}
		return {
			principal: { idUnidad: objetivo.idUnidadPrincipal, nombre: nombreDe(objetivo.idUnidadPrincipal), cambia: true },
			adicionales: objetivo.idUnidadesAdicionales.map((idUnidad) => ({
				idUnidad,
				nombre: nombreDe(idUnidad),
				marca: 'nueva' as const,
				eraPrincipal: false,
			})),
			lineasCerradas: [],
			violaciones: [],
			mensajes: [],
			sinCambios: false,
		};
	}

	const efecto = calcularEfectoAdscripcion({
		actual,
		items: entrada.items,
		decisiones: entrada.decisiones,
		objetivo,
		ordenAdicionales: entrada.ordenAdicionales,
		lineasActivas: entrada.lineasActivas,
	});
	const { final } = efecto;
	const antes = new Set(actual.idUnidadesAdicionales);
	const quedan = new Set(final.idUnidadesAdicionales);

	const adicionales: UnidadVistaPrevia[] = final.idUnidadesAdicionales.map((idUnidad) => ({
		idUnidad,
		nombre: nombreDe(idUnidad),
		marca: antes.has(idUnidad) ? 'sin_cambio' : 'nueva',
		eraPrincipal: idUnidad === actual.idUnidadPrincipal,
	}));

	// Additional units that leave. A promoted one (now the principal) is not "removed".
	for (const idUnidad of actual.idUnidadesAdicionales) {
		if (!quedan.has(idUnidad) && idUnidad !== final.idUnidadPrincipal) {
			adicionales.push({ idUnidad, nombre: nombreDe(idUnidad), marca: 'se_quita', eraPrincipal: false });
		}
	}
	// The previous principal leaves unless it was kept as additional.
	if (actual.idUnidadPrincipal !== final.idUnidadPrincipal && !quedan.has(actual.idUnidadPrincipal)) {
		adicionales.push({
			idUnidad: actual.idUnidadPrincipal,
			nombre: nombreDe(actual.idUnidadPrincipal),
			marca: 'se_quita',
			eraPrincipal: true,
		});
	}

	const nombreLinea = (idLinea: number) =>
		entrada.lineasActivas.find((linea) => linea.idLinea === idLinea)?.nombreLinea?.trim() || `Línea ${idLinea}`;

	return {
		principal: {
			idUnidad: final.idUnidadPrincipal,
			nombre: nombreDe(final.idUnidadPrincipal),
			cambia: final.idUnidadPrincipal !== actual.idUnidadPrincipal,
		},
		adicionales,
		lineasCerradas: efecto.lineasCerradas.map((idLinea) => ({ idLinea, nombre: nombreLinea(idLinea) })),
		violaciones: efecto.violaciones,
		mensajes: efecto.violaciones.map((violacion) => mensajeViolacionAdscripcion(violacion)),
		sinCambios: efecto.sinCambios,
	};
}

/** The reason the researcher wrote for one item, trimmed; null when there is none. */
export function motivoDeItem(item: Pick<ItemAdscripcion, 'motivo'>): string | null {
	return item.motivo?.trim() || null;
}

/**
 * Whether the request carries a reason per item (a request stored before the reason moved to its items carries only a
 * single text). Screens that show each reason under its item use it to decide whether the single text is still needed.
 */
export function hayMotivosPorItem(items: readonly Pick<ItemAdscripcion, 'motivo'>[]): boolean {
	return items.some((item) => motivoDeItem(item) !== null);
}

export type EstadoItemAdscripcion = 'aprobado' | 'rechazado' | 'pendiente';

export interface ResultadoItem {
	id: string;
	texto: string;
	estado: EstadoItemAdscripcion;
	/** The reason of the REJECTION (the admin's or the system's), not to be mistaken for the researcher's. */
	motivo: string | null;
	/** Why the researcher asked for THIS change; null for the derived "conservar" and for requests stored without it. */
	motivoSolicitud: string | null;
	/** A new principal that is also new to the researcher (one item, one decision): the screens add a "Nueva" tag. */
	nueva: boolean;
	/** `cascada` / `desactualizado` when the SYSTEM rejected it; null when the admin decided. */
	automatico: MotivoAutomaticoAdscripcion | null;
}

/**
 * The items of a request with their outcome, for the request card ("aprobado" / "rechazado" chips). Without
 * a stored resolution every item is pending; an item the resolution has no decision for is also pending
 * (never assumed approved).
 */
export function resultadoItems(
	adscripcion: Pick<NonNullable<SolicitudResponse['adscripcion']>, 'items' | 'resolucion'> | null,
	nombres: Map<number, string>,
): ResultadoItem[] {
	if (!adscripcion) return [];
	const decisiones = new Map(adscripcion.resolucion?.decisiones.map((decision) => [decision.id, decision]) ?? []);
	return adscripcion.items.map((item) => {
		const decision = decisiones.get(item.id);
		return {
			id: item.id,
			texto: describirItemAdscripcion(item, nombres),
			estado: decision ? (decision.aprobado ? 'aprobado' : 'rechazado') : 'pendiente',
			motivo: decision?.motivo ?? null,
			motivoSolicitud: motivoDeItem(item),
			nueva: item.tipo === 'cambiar_principal' && item.nueva === true,
			automatico: decision?.automatico ?? null,
		};
	});
}

/**
 * One line for a list ("2 aprobados · 1 rechazado"), or null while nothing is resolved. Pending items are
 * not counted: they carry no outcome yet.
 */
export function resumenResultadoItems(items: ResultadoItem[]): string | null {
	const aprobados = items.filter((item) => item.estado === 'aprobado').length;
	const rechazados = items.filter((item) => item.estado === 'rechazado').length;
	const partes = [
		aprobados > 0 ? `${aprobados} ${aprobados === 1 ? 'aprobado' : 'aprobados'}` : null,
		rechazados > 0 ? `${rechazados} ${rechazados === 1 ? 'rechazado' : 'rechazados'}` : null,
	].filter((parte): parte is string => parte !== null);
	return partes.length > 0 ? partes.join(' · ') : null;
}
